// UploadQueueContext — App-level home for the multi-file upload QUEUE, so it SURVIVES navigating
// away from the File Upload page and back.
//
// WHY (tuning): multi-file uploads import one at a time; the queue (each file's
// queued/active/done/dup/failed status) used to live in S3UploadPage's local state. App.jsx
// renders one section at a time, so leaving File Upload UNMOUNTED the page and destroyed the
// queue — returning showed a stale list (queued/in-flight files "vanished", reappearing only as
// they finished server-side). The sequential upload loop is a plain async function that keeps
// running after the page unmounts; by pointing its state writes at THIS always-mounted provider,
// the queue (and every file's live status) persists, so returning to File Upload immediately
// shows all files — queued, processing, or done.
import React, { createContext, useContext, useState } from "react";

const UploadQueueContext = createContext(null);

export function UploadQueueProvider({ children }) {
  // batchQueue: null = no batch; else [{ name, status: 'queued'|'active'|'done'|'dup'|'failed', detail }]
  const [batchQueue, setBatchQueue] = useState(null);
  const [batchDone, setBatchDone] = useState(false);
  // batchProjectId: which project this batch is uploading INTO — captured once when the batch
  // starts and never re-read. The queue survives navigating away (that's the whole point of this
  // provider), so it also survives SWITCHING PROJECTS — and without an owner id the File Upload
  // page would render this batch under whatever project is now selected, implying files are going
  // somewhere they aren't. The page uses this to only show the batch under its OWN project. (The
  // upload requests are pinned separately, in the pipeline, so the DATA always lands here too.)
  const [batchProjectId, setBatchProjectId] = useState(null);
  return (
    <UploadQueueContext.Provider value={{
      batchQueue, setBatchQueue, batchDone, setBatchDone, batchProjectId, setBatchProjectId,
    }}>
      {children}
    </UploadQueueContext.Provider>
  );
}

export function useUploadQueue() {
  return useContext(UploadQueueContext) || {
    batchQueue: null, setBatchQueue: () => {}, batchDone: false, setBatchDone: () => {},
    batchProjectId: null, setBatchProjectId: () => {},
  };
}
